/**
 * useFileUpload - Hook for uploading files to R2 storage
 *
 * Implements HOME-10: File upload with progress tracking
 *
 * Features:
 * - Upload single or multiple files
 * - Track upload progress and status
 * - Handle upload errors
 * - Clear completed uploads
 *
 * Note: This hook uses the attachment upload endpoint which stores
 * files in R2 with board-scoped paths for organization.
 */

import { useState, useCallback } from 'react';
import type { MessageAttachment } from '../components/chat/ChatMessage';

// ============================================================================
// TYPES
// ============================================================================

interface UseFileUploadOptions {
  /** Board ID for scoping uploads */
  boardId: string;
  /** Error callback for handling upload failures */
  onError?: (error: Error) => void;
}

/**
 * Upload progress state for a single file.
 */
interface UploadProgress {
  /** The file being uploaded */
  file: File;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current upload status */
  status: 'pending' | 'uploading' | 'complete' | 'error';
  /** Error message if status is 'error' */
  error?: string;
  /** Resulting attachment metadata if status is 'complete' */
  attachment?: MessageAttachment;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for uploading files to the API.
 *
 * @param options - Board ID and optional error callback
 * @returns Upload utilities and state
 *
 * @example
 * ```tsx
 * const { uploadFiles, uploads, isUploading } = useFileUpload({ boardId });
 *
 * // Upload files
 * const attachments = await uploadFiles(files);
 *
 * // Check progress
 * uploads.forEach(u => {
 *   console.log(`${u.file.name}: ${u.progress}%`);
 * });
 * ```
 */
export function useFileUpload({ boardId, onError }: UseFileUploadOptions) {
  // Track all uploads by unique ID
  const [uploads, setUploads] = useState<Map<string, UploadProgress>>(
    new Map()
  );

  /**
   * Upload a single file to the API.
   * Returns the attachment metadata on success, null on failure.
   */
  const uploadFile = useCallback(
    async (file: File): Promise<MessageAttachment | null> => {
      // Create unique ID for tracking this upload
      const uploadId = `${file.name}-${Date.now()}`;

      // Initialize upload progress
      setUploads((prev) =>
        new Map(prev).set(uploadId, {
          file,
          progress: 0,
          status: 'uploading',
        })
      );

      try {
        // Create form data with the file
        const formData = new FormData();
        formData.append('file', file);

        // Upload to API endpoint
        const response = await fetch(`/api/boards/${boardId}/attachments`, {
          method: 'POST',
          credentials: 'include', // Include session cookies
          body: formData,
        });

        if (!response.ok) {
          const error = await response.text();
          throw new Error(error || 'Upload failed');
        }

        // Parse attachment metadata from response
        const attachment = (await response.json()) as MessageAttachment;

        // Update progress to complete
        setUploads((prev) =>
          new Map(prev).set(uploadId, {
            file,
            progress: 100,
            status: 'complete',
            attachment,
          })
        );

        return attachment;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed');

        // Update progress to error state
        setUploads((prev) =>
          new Map(prev).set(uploadId, {
            file,
            progress: 0,
            status: 'error',
            error: error.message,
          })
        );

        // Call error callback if provided
        onError?.(error);
        return null;
      }
    },
    [boardId, onError]
  );

  /**
   * Upload multiple files in parallel.
   * Returns array of successfully uploaded attachments.
   */
  const uploadFiles = useCallback(
    async (files: File[]): Promise<MessageAttachment[]> => {
      // Upload all files in parallel
      const results = await Promise.all(files.map(uploadFile));
      // Filter out failures (null results)
      return results.filter((a): a is MessageAttachment => a !== null);
    },
    [uploadFile]
  );

  /**
   * Clear all upload progress tracking.
   * Call this after uploads are complete and handled.
   */
  const clearUploads = useCallback(() => {
    setUploads(new Map());
  }, []);

  return {
    /** Array of all upload progress states */
    uploads: Array.from(uploads.values()),
    /** Upload a single file */
    uploadFile,
    /** Upload multiple files in parallel */
    uploadFiles,
    /** Clear all upload tracking */
    clearUploads,
    /** True if any upload is currently in progress */
    isUploading: Array.from(uploads.values()).some(
      (u) => u.status === 'uploading'
    ),
  };
}
